import React, { useEffect, useImperativeHandle, useMemo } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import { Building2, Check, Loader2, ShieldCheck, Users } from 'lucide-react';
import type { RoleResponse } from '../../../services/api/types.gen';
import type {
  OrgMemberResponse,
  AgencyClinicResponse,
} from '../../clinician-manager/types';
import {
  useRoleAssignmentDialog,
  type RoleAssignmentDialogState,
} from './useRoleAssignmentDialog';
import { useStore } from '../../../store';
import { useShallow } from 'zustand/react/shallow';
import { FeatureFlag } from '../../../constants/featureFlags';
import { isAssignableClinicianRole } from '../../../utils/rbacRoles';
import {
  ClinicDisciplinesSection,
  type ClinicDisciplinesControl,
} from '../../clinician-manager/ClinicDisciplinesSection';

// ─── AgencyRoleCard ──────────────────────────────────────────────────────────

type AgencyRoleCardProps = {
  role: RoleResponse;
  selected: boolean;
  onToggle: (roleId: string) => void;
};

const AgencyRoleCard: React.FC<AgencyRoleCardProps> = React.memo(
  function AgencyRoleCard({ role, selected, onToggle }) {
    return (
      <button
        type='button'
        onClick={() => onToggle(role.id)}
        className={[
          'relative flex flex-col gap-1.5 rounded-lg border-2 p-4 text-left transition-all duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          selected
            ? 'border-primary bg-primary/5 shadow-sm'
            : 'border-border bg-card hover:border-primary/40 hover:bg-muted/40',
        ].join(' ')}
      >
        {selected && (
          <span className='absolute right-2.5 top-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground'>
            <Check className='h-3 w-3' />
          </span>
        )}
        <div className='flex items-center gap-2 pr-6'>
          <ShieldCheck
            className={`h-4 w-4 flex-shrink-0 ${selected ? 'text-primary' : 'text-muted-foreground'}`}
          />
          <span className='font-semibold text-sm leading-snug'>
            {role.name}
          </span>
        </div>
        {role.description && (
          <p className='text-xs text-muted-foreground leading-relaxed line-clamp-2'>
            {role.description}
          </p>
        )}
      </button>
    );
  }
);

// ─── ClinicRoleSection ────────────────────────────────────────────────────────

type ClinicRoleSectionProps = {
  role: RoleResponse;
  clinics: AgencyClinicResponse[];
  selectedClinicIds: Set<string>;
  onToggleClinic: (roleId: string, clinicId: string) => void;
  onSelectAll: (roleId: string, allClinicIds: string[]) => void;
  onClear: (roleId: string) => void;
  clinicDisciplines?: ClinicDisciplinesControl;
};

const ClinicRoleSection: React.FC<ClinicRoleSectionProps> = React.memo(
  function ClinicRoleSection({
    role,
    clinics,
    selectedClinicIds,
    onToggleClinic,
    onSelectAll,
    onClear,
    clinicDisciplines,
  }) {
    const allSelected =
      clinics.length > 0 && selectedClinicIds.size === clinics.length;
    const someSelected = selectedClinicIds.size > 0;
    const allClinicIds = useMemo(() => clinics.map(c => c.id), [clinics]);
    const showDisciplines =
      !!clinicDisciplines && isAssignableClinicianRole(role) && someSelected;

    return (
      <div
        className={[
          'rounded-lg border-2 transition-colors',
          someSelected
            ? 'border-primary/40 bg-primary/3'
            : 'border-border bg-card',
        ].join(' ')}
      >
        {/* Role header */}
        <div className='flex items-start justify-between gap-3 p-4'>
          <div className='flex items-start gap-2.5 min-w-0'>
            <Building2
              className={`mt-0.5 h-4 w-4 flex-shrink-0 ${someSelected ? 'text-primary' : 'text-muted-foreground'}`}
            />
            <div className='min-w-0'>
              <div className='flex items-center gap-2 flex-wrap'>
                <span className='font-semibold text-sm'>{role.name}</span>
                {someSelected && (
                  <Badge variant='default' className='text-xs'>
                    {selectedClinicIds.size}{' '}
                    {selectedClinicIds.size === 1 ? 'clinic' : 'clinics'}
                  </Badge>
                )}
              </div>
              {role.description && (
                <p className='mt-0.5 text-xs text-muted-foreground leading-relaxed'>
                  {role.description}
                </p>
              )}
            </div>
          </div>

          <div className='flex items-center gap-1.5 flex-shrink-0'>
            <button
              type='button'
              onClick={() =>
                allSelected
                  ? onClear(role.id)
                  : onSelectAll(role.id, allClinicIds)
              }
              className='text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded px-1'
            >
              {allSelected ? 'Clear all' : 'Select all'}
            </button>
          </div>
        </div>

        {/* Clinic grid */}
        {clinics.length === 0 ? (
          <p className='px-4 pb-4 text-xs text-muted-foreground'>
            No clinics available.
          </p>
        ) : (
          <div className='grid grid-cols-2 gap-x-4 gap-y-2 px-4 pb-4 sm:grid-cols-3'>
            {clinics.map(clinic => (
              <label
                key={clinic.id}
                className='flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-muted/60'
              >
                <Checkbox
                  checked={selectedClinicIds.has(clinic.id)}
                  onCheckedChange={() => onToggleClinic(role.id, clinic.id)}
                />
                <span className='truncate text-xs font-medium'>
                  {clinic.name}
                </span>
              </label>
            ))}
          </div>
        )}

        {showDisciplines && (
          <div className='border-t px-4 pb-4 pt-3'>
            <ClinicDisciplinesSection embedded {...clinicDisciplines} />
          </div>
        )}
      </div>
    );
  }
);

// ─── RoleAssignmentContent ───────────────────────────────────────────────────

export type RoleAssignmentHandle = {
  save: () => Promise<void>;
};

export type RoleAssignmentContentProps = {
  clinics: AgencyClinicResponse[];
  onClose: () => void;
  assignment: RoleAssignmentDialogState;
  hideFooter?: boolean;
  onLoadingChange?: (loading: boolean) => void;
  clinicDisciplines?: ClinicDisciplinesControl;
};

export const RoleAssignmentContent = React.memo(
  React.forwardRef<RoleAssignmentHandle, RoleAssignmentContentProps>(
    function RoleAssignmentContent(
      {
        clinics,
        onClose,
        assignment,
        hideFooter,
        onLoadingChange,
        clinicDisciplines,
      },
      ref
    ) {
      const {
        agencyRoles,
        clinicRoles,
        isLoading,
        isSaving,
        agencySelection,
        clinicSelection,
        toggleAgencyRole,
        toggleClinicForRole,
        selectAllClinicsForRole,
        clearClinicsForRole,
        handleSave,
      } = assignment;

      const { hasFeatureFlag } = useStore(
        useShallow(state => ({ hasFeatureFlag: state.hasFeatureFlag }))
      );
      const showClinicRoles = hasFeatureFlag(
        FeatureFlag.SHOW_CLINIC_INFO_IN_USER_MANAGER
      );

      const selectedAgencyCount = useMemo(
        () => [...agencySelection.values()].filter(Boolean).length,
        [agencySelection]
      );

      const selectedClinicRoleCount = useMemo(
        () => [...clinicSelection.values()].filter(s => s.size > 0).length,
        [clinicSelection]
      );

      const rolePickerClinics = clinics;

      useEffect(() => {
        onLoadingChange?.(isLoading);
      }, [isLoading, onLoadingChange]);

      useImperativeHandle(ref, () => ({ save: handleSave }), [handleSave]);

      return (
        <>
          {/* Role content */}
          {isLoading ? (
            <div className='flex flex-1 items-center justify-center py-12'>
              <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
            </div>
          ) : showClinicRoles ? (
            <Tabs defaultValue='agency' className='flex flex-col'>
              <TabsList className='mx-0 w-fit'>
                <TabsTrigger value='agency' className='gap-1.5'>
                  Agency Roles
                  {selectedAgencyCount > 0 && (
                    <Badge
                      variant='default'
                      className='h-4 min-w-4 px-1 text-[10px]'
                    >
                      {selectedAgencyCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value='clinic' className='gap-1.5'>
                  Clinic Roles
                  {selectedClinicRoleCount > 0 && (
                    <Badge
                      variant='default'
                      className='h-4 min-w-4 px-1 text-[10px]'
                    >
                      {selectedClinicRoleCount}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* Agency roles tab */}
              <TabsContent
                value='agency'
                className='mt-4 focus-visible:outline-none'
              >
                {agencyRoles.length === 0 ? (
                  <div className='flex flex-col items-center justify-center gap-2 py-12 text-center'>
                    <ShieldCheck className='h-8 w-8 text-muted-foreground/40' />
                    <p className='text-sm text-muted-foreground'>
                      No agency-level roles available.
                    </p>
                  </div>
                ) : (
                  <div className='grid grid-cols-2 gap-3 pb-2 pr-1 sm:grid-cols-2'>
                    {agencyRoles.map(role => (
                      <AgencyRoleCard
                        key={role.id}
                        role={role}
                        selected={!!agencySelection.get(role.id)}
                        onToggle={toggleAgencyRole}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Clinic roles tab */}
              <TabsContent
                value='clinic'
                className='mt-4 focus-visible:outline-none'
              >
                {clinicRoles.length === 0 ? (
                  <div className='flex flex-col items-center justify-center gap-2 py-12 text-center'>
                    <Building2 className='h-8 w-8 text-muted-foreground/40' />
                    <p className='text-sm text-muted-foreground'>
                      No clinic-level roles available.
                    </p>
                  </div>
                ) : (
                  <div className='flex flex-col gap-3 pb-2 pr-1'>
                    {clinicRoles.map(role => (
                      <ClinicRoleSection
                        key={role.id}
                        role={role}
                        clinics={rolePickerClinics}
                        selectedClinicIds={
                          clinicSelection.get(role.id) ?? new Set()
                        }
                        onToggleClinic={toggleClinicForRole}
                        onSelectAll={selectAllClinicsForRole}
                        onClear={clearClinicsForRole}
                        clinicDisciplines={clinicDisciplines}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          ) : (
            /* Agency roles only — no tabs */
            <div className='focus-visible:outline-none'>
              {agencyRoles.length === 0 ? (
                <div className='flex flex-col items-center justify-center gap-2 py-12 text-center'>
                  <ShieldCheck className='h-8 w-8 text-muted-foreground/40' />
                  <p className='text-sm text-muted-foreground'>
                    No agency-level roles available.
                  </p>
                </div>
              ) : (
                <div className='grid grid-cols-2 gap-3 pb-2 pr-1 sm:grid-cols-2'>
                  {agencyRoles.map(role => (
                    <AgencyRoleCard
                      key={role.id}
                      role={role}
                      selected={!!agencySelection.get(role.id)}
                      onToggle={toggleAgencyRole}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {!hideFooter && <Separator />}

          {!hideFooter && (
            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                type='button'
                onClick={handleSave}
                disabled={isSaving || isLoading}
              >
                {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                Save Changes
              </Button>
            </DialogFooter>
          )}
        </>
      );
    }
  )
);

// ─── RoleAssignmentDialog ────────────────────────────────────────────────────

type RoleAssignmentDialogProps = {
  member: OrgMemberResponse | null;
  clinics: AgencyClinicResponse[];
  onClose: () => void;
  onSuccess: () => void;
};

export const RoleAssignmentDialog: React.FC<RoleAssignmentDialogProps> =
  React.memo(function RoleAssignmentDialog({
    member,
    clinics,
    onClose,
    onSuccess,
  }) {
    const assignment = useRoleAssignmentDialog(member, onSuccess);

    const { roleManagerTitle } = useMemo(() => {
      if (!member) {
        return { roleManagerTitle: '' };
      }
      const first = member.first_name?.trim() ?? '';
      const last = member.last_name?.trim() ?? '';
      const email = member.email?.trim() ?? '';
      const hasFullName = Boolean(first && last);
      const subject = hasFullName ? `${first} ${last}` : email || 'this member';
      return {
        roleManagerTitle: `Manage Roles For ${subject}`,
      };
    }, [member]);

    return (
      <Dialog
        open={!!member}
        onOpenChange={open => {
          if (!open) onClose();
        }}
      >
        <DialogContent className='flex max-h-[88vh] flex-col sm:max-w-[740px]'>
          <DialogHeader className='pb-0'>
            <DialogTitle className='flex items-center gap-2'>
              <Users className='h-5 w-5 flex-shrink-0 text-muted-foreground' />
              <span className='min-w-0 break-words'>{roleManagerTitle}</span>
            </DialogTitle>
            <DialogDescription>{member?.email}</DialogDescription>
          </DialogHeader>

          <Separator />

          {/* RoleAssignmentContent renders at its natural height; the dialog
              owns scrolling. */}
          <div className='flex min-h-0 flex-1 flex-col overflow-y-auto'>
            <RoleAssignmentContent
              assignment={assignment}
              clinics={clinics}
              onClose={onClose}
            />
          </div>
        </DialogContent>
      </Dialog>
    );
  });
